import { useEffect, useState } from 'react';
import { obtenirSdk } from './sdk';
import { DANS_DISCORD } from '../api/client';

/**
 * Qui est dans la séance, en direct.
 *
 * Discord tient déjà cette liste — inutile de bâtir une présence maison à
 * coups de battements et de tables. On la lui demande, puis on s'abonne.
 *
 * Ce sont les participants de **l'activité**, pas du salon vocal : quelqu'un
 * qui parle sans avoir ouvert l'Activity n'y figure pas, et c'est le bon
 * critère — la liste répond à « qui regarde », pas à « qui est là ».
 *
 * L'abonnement seul ne suffisait pas. La première personne restait affichée
 * seule indéfiniment : l'événement d'arrivée ne lui parvenait pas, alors qu'un
 * changement d'écran — qui refait la lecture initiale — corrigeait le compte
 * aussitôt. Plutôt que de chercher pourquoi, on relit périodiquement : c'est
 * un appel local au client Discord, sans réseau, et la justesse de la liste ne
 * dépend plus d'un événement qui peut manquer.
 */

const EVENEMENT = 'ACTIVITY_INSTANCE_PARTICIPANTS_UPDATE';
const RELECTURE_MS = 8000;

/** Hors Discord, deux silhouettes pour voir la mise en page. */
const SOSIES = [
  { id: '1', username: 'developpeur', global_name: 'Développeur' },
  { id: '2', username: 'invite', global_name: 'Invité' },
];

export function useParticipants() {
  const [participants, setParticipants] = useState(DANS_DISCORD ? [] : SOSIES);

  useEffect(() => {
    if (!DANS_DISCORD) return undefined;

    let vivant = true;
    let sdkCourant = null;
    let minuteur = null;

    const recevoir = (donnees) => {
      if (vivant && Array.isArray(donnees?.participants)) {
        setParticipants(donnees.participants);
      }
    };

    const relire = async (sdk) => {
      try {
        recevoir(await sdk.commands.getInstanceConnectedParticipants());
      } catch {
        // Une liste momentanément indisponible n'a pas à remonter : la
        // prochaine relecture réessaiera.
      }
    };

    obtenirSdk()
      .then(async (sdk) => {
        sdkCourant = sdk;
        if (!vivant) return;

        // L'abonnement d'abord : entre la lecture initiale et lui, quelqu'un
        // pourrait arriver et n'apparaître qu'au changement suivant.
        await sdk.subscribe(EVENEMENT, recevoir);
        await relire(sdk);

        minuteur = setInterval(() => relire(sdk), RELECTURE_MS);
      })
      .catch((erreur) => {
        // La liste est un agrément : sans elle la séance fonctionne, et une
        // erreur ici ne doit pas remonter à l'écran.
        console.warn('Participants indisponibles :', erreur?.message ?? erreur);
      });

    return () => {
      vivant = false;
      if (minuteur) clearInterval(minuteur);
      // `sdkCourant` peut être encore nul si la promesse n'a pas abouti :
      // c'est le cas au double montage du mode strict de React.
      sdkCourant?.unsubscribe(EVENEMENT, recevoir);
    };
  }, []);

  // Les bots n'ont rien à faire dans une liste de spectateurs.
  return participants.filter((p) => !p.bot);
}
